/*
  Dip <dip-tabs> — the ONE tab engine, shared by every surface that switches a
  region between products or groups.

  It used to exist twice: once in dip-buy-box.js and once in dip-home.js, each
  guarded with `if (!customElements.get('dip-tabs'))`, so on a page loading both
  the first script to run silently decided the behaviour for the whole page. The
  two copies had drifted into DIFFERENT DOM contracts, which made that race a
  correctness problem rather than a tidiness one:

    - the buy-box copy found panels through `aria-controls` -> getElementById
      and toggled `hidden` only;
    - the home copy found panels through `[data-panel]` matched to the tab's
      `data-tab`, toggled `is-on` on tabs and `is-hidden` on panels, and set the
      initial selection on connect.

  Seven of the nine tabbed surfaces speak the data-panel contract (the bundle,
  the homepage how/testimonials, do-more, comparison, reviews, ingredients);
  only dip-pdp-tabs.liquid speaks aria-controls. So this engine supports BOTH:
  panels resolve by aria-controls when the tab names one, otherwise by
  data-panel. Nothing has to be re-marked-up, and no page depends on load order.

  Consumers either import it (`import '@theme/dip-tabs'`) or load it with a
  plain module script tag. Either way the module URL is identical, so it runs
  once.
*/

class DipTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (!this.tabs.length) return;

    // aria-controls wins where it is present: those panels are addressed by id and
    // may sit outside this element. Otherwise panels are the [data-panel] children.
    this.byAria = this.tabs.every((t) => t.getAttribute('aria-controls'));
    this.panels = this.byAria
      ? this.tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')))
      : [...this.querySelectorAll('[data-panel]')];
    if (!this.panels.length) return;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (e) => {
        const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        const next = this.tabs[(i + dir + this.tabs.length) % this.tabs.length];
        next.focus();
        this.select(next);
      });
    });

    // Sync to whatever the markup declared, so a panel cannot start out both
    // unhidden and unselected (the old buy-box copy never did this).
    this.select(this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0]);
  }

  select(active) {
    const idx = this.tabs.indexOf(active);
    this.tabs.forEach((t) => {
      const on = t === active;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((p, pi) => {
      if (!p) return;
      const on = this.byAria ? pi === idx : p.dataset.panel === active.dataset.tab;
      p.hidden = !on;
      p.classList.toggle('is-hidden', !on);
    });
  }
}

if (!customElements.get('dip-tabs')) customElements.define('dip-tabs', DipTabs);

export { DipTabs };
